import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {useDispatch, useSelector} from "react-redux";
import qs from "qs";

import {getMessages, getMessagesByChannel} from "redux/actions/messagesActions";
import {getNotifications} from "redux/actions/notificationsActions";
import {
  getLastMessage,
  getFormattedScheduleMessages,
  newUuid,
  getPredictedResponse,
} from "utilities/helperFunctions";
import {InboxContext} from "routes/MessagesPage";
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import ICONS from "connectui/icons/iconMap";
import {useFeatureFlags} from "utilities/featureUtil";

import Chat from "./Chat";
import Tabs from "connectui/component/input/Tabs";
import SvgIcon from "connectui/component/display/SvgIcon";
import FlexContainer from "connectui/component/layout/FlexContainer";
import Text from "connectui/component/display/Text";
import {MessagesContext} from "./ConversationsPanel";
import AutoMessagesPanel from "./AutoMessagesPanel";
import NotesPanel from "./NotesPanel";
import GuestLogsPanel from "./GuestLogsPanel";
import ChipText from "connectui/component/display/chip/ChipText";
import {getInboxData} from "../../../../../redux/actions/guestsActions";

const icons = {
  Messages: ICONS["message-circle"],
  Notes: ICONS["note"],
  "Auto-messages": ICONS.zap,
  "Guest Logs": ICONS.tool,
};

const useStyles = (theme) =>
  buildStyles({
    container: {
      position: "relative",
      backgroundColor: theme.colors.gray50,
      flexDirection: "column",
      width: "100%",
      flex: 1,
      overflow: "hidden",
    },
  });

const MessagesSection = ({guestId, loadingMessages, setLoadingMessages}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const flags = useFeatureFlags();
  const {setTab, notes, setNotes} = useContext(MessagesContext);
  const {startReached, setStartReached, setPredictedResponse} =
    useContext(InboxContext);
  const loadedGuest = useRef(null);
  const requestId = useRef(null);
  const current_user = useSelector(
    (state) => state.defaultReducer.current_user,
  );
  const messages = useSelector((state) => state.messagesReducer.messages);
  const guests = useSelector((state) => state.defaultReducer.guests_dict);
  const notifications_dict = useSelector(
    (state) => state.defaultReducer.notifications_dict,
  );
  const [guestMessages, setGuestMessages] = useState([]);
  const [scheduledMessagesLoading, setScheduledMessagesLoading] =
    useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [counts, setCounts] = useState({
    Notes: 0,
    "Auto-messages": 0,
    "Guest Logs": 0,
  });
  const scheduledMessagesData = useMemo(() => {
    return getFormattedScheduleMessages(notifications_dict.guest_id[guestId]);
  }, [notifications_dict, guestId]);

  const guest = useMemo(() => guests[guestId] ?? {}, [guests, guestId]);

  useLayoutEffect(() => {
    const currMessages = messages[guestId]?.messages?.slice(1) || [];
    setHasNextPage(false);
    setGuestMessages(currMessages.reverse());
    setLoadingMessages(!currMessages.length);
    setLoadingNotes(true);
    setScheduledMessagesLoading(true);
    setNotes([]);
  }, [guestId]);

  useEffect(() => {
    const currMessages = messages[guestId]?.messages?.slice(1) || [];
    setGuestMessages(currMessages.reverse());
    dispatch(getInboxData());
  }, [messages]);

  useEffect(() => {
    setCounts({
      Notes: notes.length,
      "Auto-messages": scheduledMessagesData.total,
    });
  }, [notes, scheduledMessagesData]);

  useEffect(() => {
    if (startReached[guestId] === hasNextPage)
      setHasNextPage(!startReached[guestId]);
  }, [startReached, guestId]);

  useEffect(() => {
    let isMounted = true;
    let currentRequestId = newUuid();
    requestId.current = currentRequestId;

    // Avoids calling the same API call twice
    if (loadedGuest.current === guestId) return;

    setLoadingMessages(true);
    setLoadingNotes(true);
    loadedGuest.current = guestId;
    const isNewConversation = !messages[guestId];

    if (!isNewConversation) {
      const lastMessage = getLastMessage(messages[guestId]?.messages);
      setPredictedResponse(getPredictedResponse(lastMessage));
      setLoadingMessages(false);
    }

    // Get messages
    dispatch(
      getMessages({
        guestId,
        sync: !isNewConversation,
        onSuccess: (res) => {
          if (!isMounted || currentRequestId !== requestId.current) return;
          setStartReached({
            ...startReached,
            [guestId]: !!res.messages[0].start_reached,
          });
          if (loadedGuest.current !== guestId) return;
          const lastMsg = getLastMessage(res.messages);
          setPredictedResponse(getPredictedResponse(lastMsg));
          setLoadingMessages(false);
        },
      }),
    );

    // Get notes
    dispatch(
      getMessagesByChannel({
        guestId,
        channel: "note",
        onSuccess: (res) => {
          if (!isMounted || currentRequestId !== requestId.current) return;
          if (loadedGuest.current !== guestId) return;
          setNotes((res.messages || []).slice(1));
          setLoadingNotes(false);
        },
      }),
    );

    // Get scheduled messages
    dispatch(
      getNotifications(
        qs.stringify({enso_key: current_user, guest_id: guestId}),
        true,
        () => {
          if (!isMounted || currentRequestId !== requestId.current) return;
          setScheduledMessagesLoading(false);
        },
      ),
    );

    return () => {
      isMounted = false;
      requestId.current = null;
    };
  }, [guestId]);

  useEffect(() => {
    if (flags["guest-logs"] && guestId) {
      setLoadingLogs(false);
    }
  }, [guestId, flags]);

  const handleSetMessages = (response) => {
    const olderMessages = response.messages.slice(1).reverse();
    setHasNextPage(!response.messages[0].start_reached);
    setStartReached({
      ...startReached,
      [guestId]: !!response.messages[0].start_reached,
    });
    setGuestMessages((prev) => prev.concat(olderMessages));
  };

  return (
    <FlexContainer className={classes.container}>
      <Tabs
        labels={
          flags["guest-logs"]
            ? ["Messages", "Notes", "Auto-messages", "Guest Logs"]
            : ["Messages", "Notes", "Auto-messages"]
        }
        onTabClick={(index) => setTab(index)}
        headerProps={{
          sx: {
            backgroundColor: theme.colors.white,
            padding: theme.spacing(0, 4),
            marginBottom: 0,
          },
        }}
        skeleton={{
          enabled: loadingMessages,
          items: flags["guest-logs"] ? 4 : 3,
        }}
        tabProps={{sx: {flex: 0}}}
        customLabelType={(label, index, activeTab) => (
          <FlexContainer
            alignItems="center"
            gap={theme.spacing(1)}
            sx={{paddingBottom: theme.spacing(1), whiteSpace: "nowrap"}}
          >
            <SvgIcon component={icons[label]} sx={{width: 16, height: 16}} />
            <Text
              variant="textXS"
              weight="semibold"
              color={index === activeTab ? "primary600" : "textSecondary"}
            >
              {label}
            </Text>
            {!!counts[label] && label !== "Guest Logs" && (
              <ChipText
                typographyVariant="textXS"
                text={counts[label]}
                color={activeTab === index ? "primary700" : "gray700"}
                sx={{
                  cursor: "pointer",
                  backgroundColor:
                    activeTab === index
                      ? theme.colors.primary50
                      : theme.colors.gray100,
                  "&:hover": {
                    backgroundColor:
                      activeTab === index
                        ? theme.colors.primary50
                        : theme.colors.gray100,
                  },
                }}
              />
            )}
          </FlexContainer>
        )}
      >
        <Chat
          guest={guest}
          hasNextPage={hasNextPage}
          skeleton={{enabled: loadingMessages || !guest?.guest_id}}
          messages={guestMessages}
          setMessages={handleSetMessages}
        />
        <NotesPanel
          notes={notes}
          setNotes={setNotes}
          guestId={guestId}
          skeleton={{
            enabled: loadingMessages || loadingNotes || !guest?.guest_id,
          }}
        />
        <AutoMessagesPanel
          guestId={guestId}
          scheduledMessages={scheduledMessagesData}
          skeleton={{enabled: scheduledMessagesLoading || !guest?.guest_id}}
        />
        {flags["guest-logs"] && (
          <GuestLogsPanel
            guestId={guestId}
            skeleton={{enabled: loadingLogs || !guest?.guest_id}}
          />
        )}
      </Tabs>
    </FlexContainer>
  );
};

export default MessagesSection;
